/**
 * PASO 4 DEL MENSUAL · TU PLAN NUEVO Y MI FEEDBACK DIRECTO
 *
 * Documento «El reporte mensual» (1-09-2026). Hasta ahora, al enviar el mensual, el
 * cliente veía una tarjeta con «Reporte enviado» y una línea. El documento convierte eso
 * en un paso del reporte, y con motivo: es donde se le entrega algo.
 *
 * Dos bloques, y la diferencia entre los dos es el tiempo:
 *
 *   YA LO TIENES              su informe, ahora mismo, con el botón para abrirlo.
 *   LO QUE VIENE              el programa nuevo, que lo escribe una persona.
 *
 * SIN DÍA NI HORA (Francisco, 18-10: «no hagas promesas»). Decía «Antes del próximo
 * viernes» y «Recibirás todo antes del viernes a las tres de la tarde».
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { diaLocal, hoyLocal } from '../../lib/horaEspana';
import { pasosDelMensual } from './PasosDelMensual';

const ORANGE = '#FF671F';

/** «12 de agosto», para el pie de cada foto. La de hoy se dice «Hoy». */
// EL DÍA DE LA FOTO, EN EL RELOJ DEL CLIENTE (12-10). `taken_at` es un instante en UTC:
// cortarle los diez primeros caracteres daba el día UTC, y el "Hoy" se comparaba contra
// `toISOString()`, que también es UTC. De madrugada en España (o por la tarde en América) la
// foto de hoy salía con la fecha de otro día. Es su pantalla: manda su reloj, como en Inicio.
const cuando = (iso, hoy) => {
    // Un día suelto (sin hora) ya es un día: `diaLocal` no lo pasa por ningún huso.
    const dia = diaLocal(iso);
    if (!dia) return '';
    if (dia === hoy) return 'Hoy';
    const d = new Date(`${dia}T12:00:00`);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
};

/**
 * «Y MIENTRAS TANTO, MÍRATE»: tres fotos suyas de frente, mientras espera el programa.
 *
 * El rótulo estaba puesto y debajo NO HABÍA NADA: un título que no presenta nada, que es
 * peor que no ponerlo. La maqueta enseña tres de la misma pose en tres momentos, y el
 * sentido es ese: lo que se ve comparando es el cambio, no una foto suelta.
 *
 * LA PRIMERA, UNA DEL MEDIO Y LA ÚLTIMA. Con dos, salen las dos; con una, no sale nada --
 * ni el rótulo --, porque una foto sola no compara con nada. De frente, que es la pose que
 * todo el mundo tiene: es la que se pide primero.
 */
const MientrasTantoMirate = ({ api, token }) => {
    const [fotos, setFotos] = useState(null);
    const [urls, setUrls] = useState({});

    useEffect(() => {
        if (!api) { setFotos([]); return; }
        api.get('/reports/photos')
            .then((r) => setFotos(r.data?.photos || []))
            .catch(() => setFotos([]));
    }, [api]);

    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    // `urls` NO entra en las dependencias, y por eso el «ya la tengo» se pregunta dentro
    // del `setUrls`: con `urls` fuera, cada foto que llega volvería a disparar el efecto y
    // el efecto volvería a pedirlas todas, que es un bucle que no para.
    const traer = useCallback((id) => {
        if (!id) return;
        setUrls((u) => {
            if (u[id]) return u;
            // Encogida (12-09), con el mismo criterio que el informe: se mira despacio.
            fetch(`${base}/api/reports/photos/${id}?w=1440`, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('no'))))
                .then((b) => setUrls((v) => ({ ...v, [id]: URL.createObjectURL(b) })))
                .catch(() => { });
            return { ...u, [id]: '' };      // apuntada como pedida, para no pedirla dos veces
        });
    }, [base, token]);

    const deFrente = (fotos || [])
        .filter((f) => (f.pose || 'frente') === 'frente' && f.taken_at)
        .sort((a, b) => String(a.taken_at).localeCompare(String(b.taken_at)));

    // La primera, una del medio y la última: tres momentos, no las tres últimas semanas.
    const tres = deFrente.length <= 3
        ? deFrente
        : [deFrente[0], deFrente[Math.floor((deFrente.length - 1) / 2)], deFrente[deFrente.length - 1]];

    // Por los ids y no por el array: `tres` se rehace en cada render y como dependencia no
    // pararía nunca.
    const idsDeLasTres = tres.map((f) => f.id).join(',');
    useEffect(() => {
        idsDeLasTres.split(',').filter(Boolean).forEach((id) => traer(id));
    }, [idsDeLasTres, traer]);

    if (fotos === null || tres.length < 2) return null;
    const hoy = hoyLocal();

    return (
        <div className="space-y-2" data-testid="paso4-mirate">
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-1">
                Y mientras tanto, mírate
            </p>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${tres.length}, minmax(0, 1fr))` }}>
                {tres.map((f) => (
                    <div key={f.id} className="space-y-1">
                        <div className="aspect-[3/4] rounded-xl bg-muted overflow-hidden">
                            {urls[f.id] && (
                                <img src={urls[f.id]} alt={`De frente, ${cuando(f.taken_at, hoy)}`}
                                    className="w-full h-full object-cover" />
                            )}
                        </div>
                        <p className="text-[11px] text-muted-foreground text-center">
                            {cuando(f.taken_at, hoy)}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
};

const MensualPaso4 = ({ informeId, onVerInforme, api, token, conRespuesta = true, fraseAlEnviar }) => {
    return (
        <div className="space-y-4" data-testid="mensual-paso4">
            {/* Sin plan personalizado, el paso es su informe y no «tu plan nuevo y mi
                feedback directo» (Francisco, 21-10). */}
            {/* SIN «SON 4 PASOS» desde el mensual por secciones (doc del 25-10): el rótulo del
                último paso, solo, como título de lo que le llega. */}
            <h2 className="text-2xl font-bold text-foreground uppercase" data-testid="mensual-rotulo-4"
                style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
                {pasosDelMensual(conRespuesta).rotulos[3]}
            </h2>

            {/* ── YA LO TIENES ──
                SOLO SI DE VERDAD LO TIENE. El documento del 1-09 lo da por entregado al
                momento («Te lo entrego ya»), pero desde T9 (doc 16-08) el informe no le sale
                al cliente hasta que Jesús lo revisa: se le prometió «con mi feedback», y
                enseñarle antes el montado a secas es entregarle media promesa.

                Las dos cosas no pueden ser verdad a la vez, así que aquí manda el dato: la
                tarjeta sale cuando el informe ya se puede abrir, y no sale cuando no. Lo que
                nunca hace es decirle «ya lo tienes» y dejarle sin nada que pulsar. */}
            {informeId && (
                <div className="rounded-2xl bg-card border border-border p-4 space-y-3"
                    data-testid="paso4-informe">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Ya lo tienes
                    </p>
                    <p className="text-base font-bold text-foreground">Tu informe del mes</p>
                    <button type="button" onClick={() => onVerInforme(informeId)}
                        data-testid="paso4-ver-informe"
                        className="inline-flex items-center gap-1.5 text-sm font-bold"
                        style={{ color: ORANGE }}>
                        Ver mi informe
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* ── RECIBIDO ── Solo si alguien le va a contestar: sin plan personalizado no se
                le promete nada (Francisco, 21-10). Con él, «Hemos recibido tu reporte mensual,
                nos pondremos en contacto contigo» (Francisco, 22-10). La frase la manda el
                servidor (`ficha.frase_al_enviar`), la misma de su tarjeta de Seguimiento. */}
            {conRespuesta && fraseAlEnviar && <div className="rounded-2xl border p-4"
                style={{ borderColor: `${ORANGE}55`, backgroundColor: `${ORANGE}0D` }}
                data-testid="paso4-programa">
                <p className="text-base font-bold text-foreground">{fraseAlEnviar}</p>
            </div>}

            {/* ── Y MIENTRAS TANTO, MÍRATE ── */}
            <MientrasTantoMirate api={api} token={token} />
        </div>
    );
};

export default MensualPaso4;
